import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  Mail, Save, Eye, Send, Image, Palette, Building2, 
  Globe, Instagram, Facebook, Youtube, Linkedin,
  CreditCard, RefreshCw, FileCode, Route
} from 'lucide-react';
import { emailTemplateApi } from '../lib/api';
import EmailTemplateManager from './EmailTemplateManager';
import EmailRulesManager from './EmailRulesManager';

const EmailTemplateSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    logo_url: '',
    company_name: '',
    tagline: '',
    founders: '',
    address: '',
    website: '',
    email: '',
    phone: '',
    primary_color: '#f7941d',
    secondary_text_color: '#777777',
    social_instagram: '',
    social_facebook: '',
    social_tiktok: '',
    social_youtube: '',
    social_linkedin: '',
    footer_note: '',
    iban: '',
    iban_intestatario: ''
  });
  
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('primo_contatto');
  
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const templateOptions = [
    { value: 'primo_contatto', label: 'Primo Contatto Cliente' },
    { value: 'conferma_appuntamento', label: 'Conferma Appuntamento' },
    { value: 'preventivo', label: 'Preventivo Inviato' },
    { value: 'contratto_pronto', label: 'Contratto Pronto' },
    { value: 'contratto_approvato', label: 'Contratto Approvato' },
    { value: 'fattura', label: 'Avviso Fattura' },
    { value: 'sal', label: 'Aggiornamento SAL' },
    { value: 'extra', label: 'Extra da Approvare' },
    { value: 'lead_assegnato', label: 'Lead Assegnato (Staff)' },
    { value: 'attivazione', label: 'Attivazione Account (Staff)' },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await emailTemplateApi.getSettings();
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (err) {
      toast.error('Errore nel caricamento delle impostazioni email');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await emailTemplateApi.updateSettings(settings);
      toast.success('Impostazioni email salvate');
    } catch (err) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      // First save current settings
      await emailTemplateApi.updateSettings(settings);
      // Then get preview
      const response = await emailTemplateApi.getPreview(selectedTemplate);
      setPreviewHtml(response.data.html);
      setPreviewSubject(response.data.subject);
      setPreviewOpen(true);
    } catch (err) {
      toast.error('Errore nella generazione anteprima');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Inserisci un indirizzo email');
      return;
    }
    try {
      setSendingTest(true);
      await emailTemplateApi.sendTest(selectedTemplate, testEmail);
      toast.success(`Email di test inviata a ${testEmail}`);
      setTestEmailOpen(false);
    } catch (err) {
      toast.error('Errore nell\'invio email di test');
    } finally {
      setSendingTest(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Card className="card-abitalo">
        <CardContent className="p-8 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#FF7819]" />
          <p className="mt-4 text-gray-500">Caricamento impostazioni...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="card-abitalo">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#FF7819]" />
                Template Email
              </CardTitle>
              <CardDescription>
                Personalizza l&apos;aspetto delle email inviate dal CRM
              </CardDescription>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-[#FF7819] hover:bg-[#e66a0f]">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvataggio...' : 'Salva Modifiche'}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Settings Tabs */}
      <Tabs defaultValue="templates" className="space-y-4">
        <TabsList className="bg-gray-100 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="templates">
            <FileCode className="w-4 h-4 mr-2" />
            Template Configurabili
          </TabsTrigger>
          <TabsTrigger value="rules" data-testid="email-rules-tab-trigger">
            <Route className="w-4 h-4 mr-2" />
            Regole Invio
          </TabsTrigger>
          <TabsTrigger value="branding">
            <Image className="w-4 h-4 mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="company">
            <Building2 className="w-4 h-4 mr-2" />
            Azienda
          </TabsTrigger>
          <TabsTrigger value="social">
            <Globe className="w-4 h-4 mr-2" />
            Social
          </TabsTrigger>
          <TabsTrigger value="payment">
            <CreditCard className="w-4 h-4 mr-2" />
            Pagamenti
          </TabsTrigger>
          <TabsTrigger value="preview">
            <Eye className="w-4 h-4 mr-2" />
            Anteprima
          </TabsTrigger>
        </TabsList>

        {/* TEMPLATE CONFIGURABILI */}
        <TabsContent value="templates">
          <EmailTemplateManager />
        </TabsContent>

        {/* REGOLE INVIO EMAIL */}
        <TabsContent value="rules">
          <EmailRulesManager />
        </TabsContent>

        {/* BRANDING */}
        <TabsContent value="branding">
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Logo e Colori</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>URL Logo</Label>
                  <Input
                    value={settings.logo_url}
                    onChange={(e) => handleChange('logo_url', e.target.value)}
                    placeholder="https://www.example.com/logo.png"
                  />
                  <p className="text-xs text-gray-500">Inserisci l&apos;URL completo del logo (formato PNG o JPG)</p>
                </div>

                {settings.logo_url && (
                  <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                    <img 
                      src={settings.logo_url} 
                      alt="Logo preview" 
                      className="max-h-20 object-contain"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Colore Primario</Label>
                  <div className="flex gap-3">
                    <Input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      className="w-16 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      placeholder="#f7941d"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Usato per titoli, pulsanti e link</p>
                </div>

                <div className="space-y-2">
                  <Label>Colore Testo Secondario</Label>
                  <div className="flex gap-3">
                    <Input
                      type="color"
                      value={settings.secondary_text_color}
                      onChange={(e) => handleChange('secondary_text_color', e.target.value)}
                      className="w-16 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.secondary_text_color}
                      onChange={(e) => handleChange('secondary_text_color', e.target.value)}
                      placeholder="#777777"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Usato per tagline e testi secondari</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Nome Azienda (Brand)</Label>
                  <Input
                    value={settings.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    placeholder="Abitalo"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tagline</Label>
                  <Input
                    value={settings.tagline}
                    onChange={(e) => handleChange('tagline', e.target.value)}
                    placeholder="Ristrutturazioni chiavi in mano"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Firma Fondatori</Label>
                <Input
                  value={settings.founders}
                  onChange={(e) => handleChange('founders', e.target.value)}
                  placeholder="Antonio & Gaetano"
                />
                <p className="text-xs text-gray-500">Appare nelle email ai clienti come firma personale</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMPANY INFO */}
        <TabsContent value="company">
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Informazioni Aziendali</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Indirizzo</Label>
                  <Input
                    value={settings.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="Via Pacifico 59, Capurso (BA)"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sito Web</Label>
                  <Input
                    value={settings.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="www.abitalo.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={settings.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="info@abitalo.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Telefono (opzionale)</Label>
                  <Input
                    value={settings.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+39 080 1234567"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SOCIAL */}
        <TabsContent value="social">
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Link Social Media</CardTitle>
              <CardDescription>I link ai social appaiono nel footer delle email</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Instagram className="w-4 h-4" />
                    Instagram
                  </Label>
                  <Input
                    value={settings.social_instagram}
                    onChange={(e) => handleChange('social_instagram', e.target.value)}
                    placeholder="https://www.instagram.com/..."
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Facebook className="w-4 h-4" />
                    Facebook
                  </Label>
                  <Input
                    value={settings.social_facebook}
                    onChange={(e) => handleChange('social_facebook', e.target.value)}
                    placeholder="https://www.facebook.com/..."
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                    TikTok
                  </Label>
                  <Input
                    value={settings.social_tiktok}
                    onChange={(e) => handleChange('social_tiktok', e.target.value)}
                    placeholder="https://www.tiktok.com/..."
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Youtube className="w-4 h-4" />
                    YouTube
                  </Label>
                  <Input
                    value={settings.social_youtube}
                    onChange={(e) => handleChange('social_youtube', e.target.value)}
                    placeholder="https://www.youtube.com/..."
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                  </Label>
                  <Input
                    value={settings.social_linkedin}
                    onChange={(e) => handleChange('social_linkedin', e.target.value)}
                    placeholder="https://www.linkedin.com/..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PAYMENT */}
        <TabsContent value="payment">
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Informazioni Pagamento</CardTitle>
              <CardDescription>Questi dati appaiono nelle email di avviso fattura</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Intestatario IBAN</Label>
                <Input
                  value={settings.iban_intestatario}
                  onChange={(e) => handleChange('iban_intestatario', e.target.value)}
                  placeholder="L & F Costruzioni S.r.l."
                />
              </div>

              <div className="space-y-2">
                <Label>IBAN</Label>
                <Input
                  value={settings.iban}
                  onChange={(e) => handleChange('iban', e.target.value)}
                  placeholder="IT00 X000 0000 0000 0000 0000 000"
                />
                <p className="text-xs text-gray-500">Lascia vuoto se non vuoi mostrare l&apos;IBAN nelle email</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PREVIEW */}
        <TabsContent value="preview">
          <Card className="card-abitalo">
            <CardHeader>
              <CardTitle>Anteprima Template</CardTitle>
              <CardDescription>Seleziona un template e visualizza l&apos;anteprima con le tue impostazioni</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Label>Seleziona Template</Label>
                  <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {templateOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3 items-end">
                  <Button 
                    onClick={handlePreview} 
                    disabled={previewLoading}
                    className="bg-[#FF7819] hover:bg-[#e66a0f]"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {previewLoading ? 'Generazione...' : 'Visualizza Anteprima'}
                  </Button>

                  <Button 
                    variant="outline"
                    onClick={() => setTestEmailOpen(true)}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Invia Test
                  </Button>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Template disponibili:</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-600">
                  <div>• Primo contatto</div>
                  <div>• Conferma appuntamento</div>
                  <div>• Preventivo inviato</div>
                  <div>• Contratto pronto</div>
                  <div>• Contratto approvato</div>
                  <div>• Avviso fattura</div>
                  <div>• Aggiornamento SAL</div>
                  <div>• Extra da approvare</div>
                  <div>• Lead assegnato</div>
                  <div>• Attivazione account</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Anteprima Email</DialogTitle>
            <p className="text-sm text-gray-500">
              <strong>Oggetto:</strong> {previewSubject}
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-auto border rounded-lg bg-gray-100 p-4">
            <div 
              className="bg-white mx-auto"
              style={{ maxWidth: '600px' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailOpen} onOpenChange={setTestEmailOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia Email di Test</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Template</Label>
              <p className="text-sm font-medium">
                {templateOptions.find(t => t.value === selectedTemplate)?.label}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Email Destinatario</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSendTest} 
              disabled={sendingTest}
              className="bg-[#FF7819] hover:bg-[#e66a0f]"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingTest ? 'Invio...' : 'Invia Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplateSettings;
